import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Eye, EyeOff } from "lucide-react";
import SharedLayout from "./SharedLayout";
import { useAuth } from "@/hooks/useAuth";
import { useEffect } from "react";
import { BASE_URL } from "@/config/api.config";
const GoogleLoginButton = ({ disabled }) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  // Handle Google callback - this runs when component mounts
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const encodedUserData = params.get("userData");
    const error = params.get("error");

    // If no Google auth parameters, do nothing
    if (!token && !encodedUserData && !error) {
      return;
    }

    // Clear the URL parameters
    window.history.replaceState({}, document.title, window.location.pathname);

    if (error) {
      toast.error(
        error === "auth_failed"
          ? "Authentication failed"
          : error === "no_profile"
          ? "No profile found"
          : "Login failed: " + error
      );
      return;
    }

    if (token && encodedUserData) {
      try {
        const userData = JSON.parse(atob(encodedUserData));
        const loginSuccess = login(userData, token);

        if (loginSuccess) {
          toast.success("Google login successful!");
          setTimeout(() => {
            const pendingReferralCode = localStorage.getItem("pendingReferralCode");
            
            if (pendingReferralCode && userData.role !== "admin" && userData.role !== "expert") {
              navigate("/referral", { replace: true });
            } else {
              switch (userData.role) {
                case "admin":
                  navigate("/admindashboard", { replace: true });
                  break;
                case "expert":
                  navigate("/expert-dashboard", { replace: true });
                  break;
                default:
                  navigate("/dashboard", { replace: true });
                  break;
              }
            }
          }, 100);
        }
      } catch (error) {
        console.error("Error processing authentication data:", error);
        toast.error("Failed to process login data");
      }
    }
  }, [login, navigate]);

  const handleGoogleLogin = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isLoading || disabled) return;

    setIsLoading(true);

    try {
      const returnUrl = `${window.location.origin}${location.pathname}`;
      window.location.href = `${BASE_URL}/api/v1/auth/google?returnUrl=${encodeURIComponent(
        returnUrl
      )}`;
    } catch (error) {
      console.error("Error initiating Google login:", error);
      toast.error("Failed to connect to Google login");
      setIsLoading(false);
    }
  };

  return (
    <button
      type="button" // Important: prevents form submission
      onClick={handleGoogleLogin}
      disabled={isLoading || disabled}
      className="w-full flex items-center justify-center gap-3 py-3.5 px-4 bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-gray-300 rounded-lg font-semibold text-gray-700 shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
      tabIndex="3"
    >
      {isLoading ? (
        <svg
          className="animate-spin h-5 w-5 text-gray-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      ) : (
        <>
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
            <path d="M1 1h22v22H1z" fill="none" />
          </svg>
          <span>Continue with Google</span>
        </>
      )}
    </button>
  );
};

export default GoogleLoginButton;
